import { createReducer, on } from '@ngrx/store';
import {
  ApplicationAppEntityApplicationRead,
  EntityTypeAppEntityDictionaryEntityType,
  RoadOccupationTypeAppEntityDictionaryRoadOccupationType,
  ShipmentAppEntityShipmentRead,
  TreeRemovalReasonAppEntityDictionaryTreeRemovalReason
} from '../../openapi';
import {
  approveApplication,
  approveApplicationFailure,
  approveApplicationSuccess,
  createApplication,
  createApplicationFailure,
  createApplicationSuccess,
  fakeSignApplication,
  fakeSignApplicationFailure,
  fakeSignApplicationSuccess,
  loadApplication,
  loadApplicationFailure,
  loadApplicationList,
  loadApplicationListFailure,
  loadApplicationListSuccess,
  loadApplicationSuccess,
  loadEntityTypes,
  loadEntityTypesFailure,
  loadEntityTypesSuccess,
  loadPayments,
  loadPaymentsFailure,
  loadPaymentsSuccess,
  loadRoadOccupationTypes,
  loadRoadOccupationTypesFailure,
  loadRoadOccupationTypesSuccess,
  loadServiceAttachmentPositions,
  loadServiceAttachmentPositionsFailure,
  loadServiceAttachmentPositionsSuccess,
  loadShipment,
  loadShipmentFailure,
  loadShipments,
  loadShipmentsFailure,
  loadShipmentsSuccess,
  loadShipmentSuccess,
  loadTreeRemovalReasons,
  loadTreeRemovalReasonsFailure,
  loadTreeRemovalReasonsSuccess,
  sendApplication,
  sendApplicationFailure,
  sendApplicationSuccess,
  setApplication,
  setPayments,
  setShipments,
  signApplication,
  signApplicationFailure,
  signApplicationSuccess,
  updateApplicants,
  updateApplicantsFailure,
  updateApplicantsSuccess,
  updateApplication,
  updateApplicationAttachments,
  updateApplicationAttachmentsFailure,
  updateApplicationAttachmentsSuccess,
  updateApplicationFailure,
  updateApplicationSuccess,
  updateAssureReceptionWater,
  updateAssureReceptionWaterFailure,
  updateAssureReceptionWaterSuccess,
  updateContractors,
  updateContractorsFailure,
  updateContractorsSuccess,
  updateInvestorRepresentatives,
  updateInvestorRepresentativesFailure,
  updateInvestorRepresentativesSuccess,
  updateLargeFamilyCards,
  updateLargeFamilyCardsFailure,
  updateLargeFamilyCardsSuccess,
  updateOccupations,
  updateOccupationsFailure,
  updateOccupationsSuccess,
  updateParcels,
  updateParcelsFailure,
  updateParcelsSuccess,
  updateTreeCuttingInfo,
  updateTreeCuttingInfoFailure,
  updateTreeCuttingInfoSuccess
} from '../actions/application.actions';
import { ApplicationListItem, ApplicationPayment, AttachmentPositions } from '../../services/application.common.model';

export interface ApplicationState {
  loading: number;
  pending: number;
  application: ApplicationAppEntityApplicationRead | null;
  applicationPayment: ApplicationPayment | null;
  shipments: ShipmentAppEntityShipmentRead[];
  shipment: ShipmentAppEntityShipmentRead | null;
  applicationList: ApplicationListItem[];
  roadOccupationTypes: RoadOccupationTypeAppEntityDictionaryRoadOccupationType[];
  treeRemovalReasons: TreeRemovalReasonAppEntityDictionaryTreeRemovalReason[];
  entityTypes: EntityTypeAppEntityDictionaryEntityType[];
  serviceAttachmentPositions: AttachmentPositions;
}

export const initialApplicationState: ApplicationState = {
  pending: 0,
  loading: 0,
  application: null,
  applicationPayment: null,
  shipments: [],
  shipment: null,
  applicationList: [],
  roadOccupationTypes: [],
  treeRemovalReasons: [],
  entityTypes: [],
  serviceAttachmentPositions: []
};

export const applicationReducer = createReducer(
  initialApplicationState,
  on(createApplication, state => ({ ...state, pending: state.pending + 1 })),
  on(createApplicationSuccess, (state, action) => ({
    ...state,
    application: action.application,
    pending: state.pending - 1
  })),
  on(createApplicationFailure, state => ({ ...state, pending: state.pending - 1 })),

  on(setApplication, (state, action) => ({ ...state, application: action.application })),

  on(loadApplication, state => ({ ...state, loading: state.loading + 1 })),
  on(loadApplicationSuccess, (state, action) => ({
    ...state,
    loading: state.loading - 1,
    application: action.application
  })),
  on(loadApplicationFailure, state => ({ ...state, loading: state.loading - 1 })),

  on(updateApplication, state => ({ ...state, pending: state.pending + 1 })),
  on(updateApplicationSuccess, (state, action) => ({
    ...state,
    pending: state.pending - 1,
    application: action.application
  })),
  on(updateApplicationFailure, state => ({ ...state, pending: state.pending - 1 })),

  on(updateApplicants, state => ({ ...state, pending: state.pending + 1 })),
  on(updateApplicantsSuccess, (state, action) => ({
    ...state,
    application: action.application,
    pending: state.pending - 1
  })),
  on(updateApplicantsFailure, state => ({ ...state, pending: state.pending - 1 })),

  on(updateContractors, state => ({ ...state, pending: state.pending + 1 })),
  on(updateContractorsSuccess, (state, action) => ({
    ...state,
    application: action.application,
    pending: state.pending - 1
  })),
  on(updateContractorsFailure, state => ({ ...state, pending: state.pending - 1 })),

  on(updateOccupations, state => ({ ...state, pending: state.pending + 1 })),
  on(updateOccupationsSuccess, (state, action) => ({
    ...state,
    pending: state.pending - 1,
    application: action.application
  })),
  on(updateOccupationsFailure, state => ({ ...state, pending: state.pending - 1 })),

  on(updateApplicationAttachments, state => ({ ...state, pending: state.pending + 1 })),
  on(updateApplicationAttachmentsSuccess, (state, action) => ({
    ...state,
    application: action.application,
    pending: state.pending - 1
  })),
  on(updateApplicationAttachmentsFailure, state => ({ ...state, pending: state.pending - 1 })),

  on(approveApplication, state => ({ ...state, pending: state.pending + 1 })),
  on(approveApplicationSuccess, (state) => ({
    ...state,
    pending: state.pending - 1
  })),
  on(approveApplicationFailure, state => ({ ...state, pending: state.pending - 1 })),

  on(loadApplicationList, state => ({ ...state, loading: state.loading + 1 })),
  on(loadApplicationListSuccess, (state, action) => ({
    ...state,
    loading: state.loading - 1,
    applicationList: action.applicationList
  })),
  on(loadApplicationListFailure, (state) => ({ ...state, loading: state.loading - 1 })),

  on(loadRoadOccupationTypes, state => ({ ...state, loading: state.loading + 1 })),
  on(loadRoadOccupationTypesSuccess, (state, action) => ({
      ...state,
      roadOccupationTypes: action.roadOccupationTypes,
      loading: state.loading - 1
    })
  ),
  on(loadRoadOccupationTypesFailure, state => ({ ...state, loading: state.loading - 1 })),

  on(setPayments, (state, action) => ({ ...state, applicationPayment: action.applicationPayment })),

  on(loadPayments, state => ({ ...state, loading: state.loading + 1 })),
  on(loadPaymentsSuccess, (state, action) => ({
      ...state,
      applicationPayment: action.applicationPayment,
      loading: state.loading - 1
    })
  ),
  on(loadPaymentsFailure, state => ({ ...state, loading: state.loading - 1 })),

  on(sendApplication, state => ({ ...state, pending: state.pending + 1 })),
  on(sendApplicationSuccess, state => ({ ...state, pending: state.pending - 1 })),
  on(sendApplicationFailure, state => ({ ...state, pending: state.pending - 1 })),

  on(signApplication, state => ({ ...state, pending: state.pending + 1 })),
  on(signApplicationSuccess, state => ({ ...state, pending: state.pending - 1 })),
  on(signApplicationFailure, state => ({ ...state, pending: state.pending - 1 })),

  on(setShipments, (state, action) => ({ ...state, shipments: action.shipments })),

  on(loadShipments, state => ({ ...state, loading: state.loading + 1 })),
  on(loadShipmentsSuccess, (state, action) => ({
    ...state,
    shipments: action.shipments,
    loading: state.loading - 1
  })),
  on(loadShipmentsFailure, state => ({ ...state, loading: state.loading - 1 })),

  on(loadShipment, state => ({ ...state, loading: state.loading + 1 })),
  on(loadShipmentSuccess, (state, action) => ({
      ...state,
      shipment: action.shipment,
      loading: state.loading - 1
    })
  ),
  on(loadShipmentFailure, state => ({ ...state, loading: state.loading - 1 })),

  on(updateParcels, state => ({ ...state, pending: state.pending + 1 })),
  on(updateParcelsSuccess, (state, action) => ({
    ...state,
    application: action.application,
    pending: state.pending - 1
  })),
  on(updateParcelsFailure, state => ({ ...state, pending: state.pending - 1 })),

  on(updateLargeFamilyCards, state => ({ ...state, pending: state.pending + 1 })),
  on(updateLargeFamilyCardsSuccess, (state, action) => ({
    ...state,
    pending: state.pending - 1,
    application: action.application
  })),
  on(updateLargeFamilyCardsFailure, state => ({ ...state, pending: state.pending - 1 })),

  on(loadTreeRemovalReasons, state => ({ ...state, loading: state.loading + 1 })),
  on(loadTreeRemovalReasonsSuccess, (state, action) => ({
    ...state,
    loading: state.loading - 1,
    treeRemovalReasons: action.treeRemovalReasons
  })),
  on(loadTreeRemovalReasonsFailure, state => ({ ...state, loading: state.loading - 1 })),
  on(updateTreeCuttingInfo, state => ({ ...state, pending: state.pending + 1 })),
  on(updateTreeCuttingInfoSuccess, (state, action) => ({
    ...state,
    pending: state.pending - 1,
    application: action.application
  })),
  on(updateTreeCuttingInfoFailure, state => ({ ...state, pending: state.pending - 1 })),

  on(updateInvestorRepresentatives, state => ({ ...state, pending: state.pending + 1 })),
  on(updateInvestorRepresentativesSuccess, (state, action) => ({
    ...state,
    pending: state.pending - 1,
    application: action.application
  })),
  on(updateInvestorRepresentativesFailure, (state, action) => ({ ...state, pending: state.pending - 1 })),

  on(fakeSignApplication, state => ({ ...state, pending: state.pending + 1 })),
  on(fakeSignApplicationSuccess, (state, action) => ({
    ...state,
    pending: state.pending - 1
  })),
  on(fakeSignApplicationFailure, state => ({ ...state, pending: state.pending - 1 })),
  on(loadEntityTypes, state => ({ ...state, loading: state.loading + 1 })),
  on(loadEntityTypesSuccess, (state, action) => ({
    ...state,
    entityTypes: action.entityTypes,
    loading: state.loading - 1
  })),
  on(loadEntityTypesFailure, state => ({ ...state, loading: state.loading - 1 })),
  on(updateAssureReceptionWater, state => ({ ...state, pending: state.pending + 1 })),
  on(updateAssureReceptionWaterSuccess, (state, action) => ({
    ...state,
    pending: state.pending - 1,
    application: action.application
  })),
  on(updateAssureReceptionWaterFailure, state => ({ ...state, pending: state.pending - 1 })),
  on(loadServiceAttachmentPositions, state => ({ ...state, loading: state.loading + 1 })),
  on(loadServiceAttachmentPositionsSuccess, (state, action) => ({
    ...state,
    loading: state.loading - 1,
    serviceAttachmentPositions: action.serviceAttachmentPositions
  })),
  on(loadServiceAttachmentPositionsFailure, state => ({ ...state, loading: state.loading - 1 }))
);
